import { useEffect } from 'react';
import PropTypes from 'prop-types';
import useVersionComparison from './useVersionComparison';
import { buildSideBySideRows } from './comparison-model';
import { WORKING_COPY_VALUE } from './compare-constants';
import VersionSelect from './VersionSelect';
import FieldDiffColumns from './FieldDiffColumns';
import SideBySideSection from './SideBySideSection';
import RuleDiffSection from './RuleDiffSection';
import CompareContextHeader from './CompareContextHeader';

const DEFAULT_ELEMENT_NAME = 'Data Element';

// First version that carries an elementName wins; otherwise a stable fallback.
function deriveElementName(versions) {
	const named = (versions || []).find((v) => v && v.elementName);
	return (named && named.elementName) || DEFAULT_ELEMENT_NAME;
}

// Base/target head labels: working copy → its label; else v{value}.
function headLabel(value, workingCopy, side) {
	if (value === WORKING_COPY_VALUE) {
		const label = (workingCopy && workingCopy.label) || 'Working copy';
		return `${label} (${side})`;
	}
	return `v${value} (${side})`;
}

// Adapt the field-order diff to SideBySideSection's {key, before, after} shape.
// A reorder is a MODIFICATION (before order → after order), not an addition —
// so both slots map to `modified` and surface the full before/after ordering.
function buildFieldOrderDiff(fieldOrder) {
	const empty = { added: [], removed: [], modified: [] };
	if (!fieldOrder) return empty;
	const modified = [];
	if (fieldOrder.properties) {
		modified.push({
			key: 'properties order',
			before: fieldOrder.properties.before.join(', '),
			after: fieldOrder.properties.after.join(', '),
		});
	}
	if (fieldOrder.required) {
		modified.push({
			key: 'required order',
			before: fieldOrder.required.before.join(', '),
			after: fieldOrder.required.after.join(', '),
		});
	}
	return { ...empty, modified };
}

function VersionComparison({ versions, workingCopy, statuses, initialBase, initialTarget, initialOnlyChanges, onSelectionChange, compact }) {
	const {
		options, base, target, setBase, setTarget,
		onlyChanges, setOnlyChanges, leftSchema, rightSchema,
		comparison, sameSide, baseVersion, targetVersion,
	} = useVersionComparison({ versions, workingCopy, statuses, initialBase, initialTarget, initialOnlyChanges });

	useEffect(() => {
		if (onSelectionChange) onSelectionChange({ base, target, onlyChanges });
	}, [base, target, onlyChanges]); // eslint-disable-line react-hooks/exhaustive-deps

	const { summary } = comparison;
	const fieldOrderDiff = buildFieldOrderDiff(comparison.fieldOrder);

	const rows = buildSideBySideRows(comparison, { onlyChanges, leftSchema, rightSchema });

	const elementName = deriveElementName(versions);
	const baseHeadLabel = headLabel(base, workingCopy, 'base');
	const targetHeadLabel = headLabel(target, workingCopy, 'target');

	function swap() {
		const b = base;
		setBase(target);
		setTarget(b);
	}

	return (
		<div className="dsv-compare">
			<div className="dsv-compare__toolbar">
				<VersionSelect label="Base" value={base} options={options} onChange={setBase} testId="dsv-compare-base-select" />
				<button type="button" className="dsv-compare__swap" data-testid="dsv-compare-swap" onClick={swap} aria-label="Swap sides">
					<svg
						className="dsv-compare__swap-icon"
						width="17"
						height="17"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.2"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
						focusable="false"
					>
						<path d="M4 9h16" />
						<path d="M17 6l3 3-3 3" />
						<path d="M20 15H4" />
						<path d="M7 12l-3 3 3 3" />
					</svg>
				</button>
				<VersionSelect label="Compare against" value={target} options={options} onChange={setTarget} testId="dsv-compare-target-select" />
				<label className="dsv-compare__toggle">
					<input type="checkbox" data-testid="dsv-compare-only-changes" checked={onlyChanges} onChange={(e) => setOnlyChanges(e.target.checked)} />
					Only changes
				</label>
			</div>

			<CompareContextHeader
				baseVersion={baseVersion}
				targetVersion={targetVersion}
				summary={summary}
				elementName={elementName}
				compact={compact}
			/>

			{sameSide ? (
				<div className="dsv-compare__empty">Comparing a version against itself — no differences.</div>
			) : !summary.hasChanges ? (
				<div className="dsv-compare__empty">No differences between the selected versions.</div>
			) : (
				<>
					<div className="dsv-compare__sheet">
						<div className="dsv-compare__sheet-title">Fields</div>
						<div className="dsv-compare__heads">
							<div className="dsv-compare__head">{baseHeadLabel}</div>
							<div className="dsv-compare__head">{targetHeadLabel}</div>
						</div>
						<FieldDiffColumns rows={rows} />
					</div>
					<RuleDiffSection rules={comparison.rules} />
					<SideBySideSection title="Root Metadata" diff={comparison.rootMeta} />
					<SideBySideSection title="Custom Validators" diff={comparison.customValidators} />
					<SideBySideSection title="Field Ordering" diff={fieldOrderDiff} />
				</>
			)}
		</div>
	);
}

VersionComparison.propTypes = {
	versions: PropTypes.array,
	workingCopy: PropTypes.shape({ label: PropTypes.string, schema: PropTypes.object }),
	statuses: PropTypes.arrayOf(PropTypes.string),
	initialBase: PropTypes.string,
	initialTarget: PropTypes.string,
	initialOnlyChanges: PropTypes.bool,
	onSelectionChange: PropTypes.func,
	compact: PropTypes.bool,
};

VersionComparison.defaultProps = {
	initialOnlyChanges: true,
	compact: false,
};

export default VersionComparison;
